<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { DashInfoCard, DashInfoWidget, Loader } from '@Shelly/shelly-design-system';
	import { devices, devices_settings } from 'src/stores';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getConfiguredEm3ChannelName } from 'src/helpers/em3_channel_names';
	import type { DashEnergyPeakVariant } from 'src/components/Dashboards/dash-energy-types';
	import {
		fetchPeakPowerSnapshots,
		type PeakPowerDeviceSnapshot,
	} from 'src/components/EnergyMonitoring/helpers/fetch_peak_power_statistics';
	import {
		buildPeakDevicePanels,
		buildPeakMeterRows,
		buildPeakPowerOverviewModel,
		buildPhasePeakBalanceModel,
		type PeakDevicePanel,
	} from 'src/components/EnergyMonitoring/helpers/energy_peak_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { DEMO_PEAK_PANELS } from 'src/components/EnergyMonitoring/helpers/energy_demo_providers';
	import EmWidgetState from '../EmWidgetState.svelte';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmPeakPowerOverview from './EmPeakPowerOverview.svelte';
	import EmPeakPowerByMeter from './EmPeakPowerByMeter.svelte';
	import EmPhasePeakBalance from './EmPhasePeakBalance.svelte';
	import {
		ENERGY_ADD_DEVICE_HREF,
		getEnergyTariffSettingsHref,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let compact = false;
	export let variant: DashEnergyPeakVariant = 'overview';
	export let staticPreview = false;

	let loading = true;
	let loadError = false;
	let snapshots: PeakPowerDeviceSnapshot[] = [];
	let loadToken = 0;
	let refreshTick = 0;
	let refreshTimer: ReturnType<typeof setInterval> | undefined;
	let devicePanels: PeakDevicePanel[] = [];

	$: useDemoData = staticPreview || $energyMonitoringDataMode === 'demo';

	const loadSnapshots = async () => {
		if (useDemoData) {
			snapshots = [];
			loadError = false;
			loading = false;
			return;
		}

		const token = ++loadToken;
		loading = true;
		loadError = false;
		try {
			const result = await fetchPeakPowerSnapshots();
			if (token !== loadToken) return;
			snapshots = result;
		} catch {
			if (token !== loadToken) return;
			snapshots = [];
			loadError = true;
		} finally {
			if (token === loadToken) loading = false;
		}
	};

	const requestSnapshotRefresh = (
		_useDemoData: boolean,
		_peakDeviceKey: string,
		_refreshTick: number,
	) => loadSnapshots();

	$: peakDeviceKey = Object.values($devices)
		.filter((device) => device && $devices[real_id(device.id)]?.cloud_options?.measure_peak_power)
		.map((device) => device.id)
		.join(',');
	$: hasDevices = useDemoData || peakDeviceKey.length > 0;

	$: void requestSnapshotRefresh(useDemoData, peakDeviceKey, refreshTick);

	onMount(() => {
		refreshTimer = setInterval(() => {
			refreshTick += 1;
		}, 60_000);
	});

	onDestroy(() => {
		loadToken += 1;
		if (refreshTimer) clearInterval(refreshTimer);
	});

	const phaseLetter = (channel: number) => ['A', 'B', 'C'][channel] ?? String(channel + 1);

	const getPhaseLabel = (device, channel: number, channelCount: number) => {
		const configuredName = getConfiguredEm3ChannelName(device, channel);
		if (configuredName) return configuredName;
		if (channelCount <= 1) {
			return $format('label_io_channel', { default: 'Channel' });
		}

		return $format('energy_monitoring_phase_label', {
			default: 'Phase {phase}',
			values: { phase: phaseLetter(channel) },
		});
	};

	// Reading the settings store here rebuilds labels immediately after a channel rename.
	$: {
		$devices_settings;
		devicePanels = useDemoData
			? DEMO_PEAK_PANELS
			: buildPeakDevicePanels(snapshots, {
					phaseLabel: getPhaseLabel,
				});
	}
	$: overviewModel = buildPeakPowerOverviewModel(devicePanels);
	$: meterRows = buildPeakMeterRows(devicePanels);
	$: phaseBalanceModel = buildPhasePeakBalanceModel(devicePanels);
	$: quickPeaks = meterRows.slice(0, 4).map((row) => ({
		...row.strongest,
		deviceName: row.panel.deviceName,
	}));
	$: resolvedTitle =
		variant === 'byMeter'
			? $format('energy_monitoring_peak_by_meter_title', { default: 'Peak Power by Meter' })
			: variant === 'phaseBalance'
				? $format('energy_monitoring_phase_peak_balance_title', {
						default: 'Phase Peak Balance',
					})
				: $format('energy_monitoring_peak_overview_title', {
						default: 'Peak Power Overview',
					});
</script>

{#if compact}
	{#if quickPeaks.length}
		<DashInfoWidget
			aria-label={$format('energy_monitoring_peak_quick_summary', {
				default: 'Peak power quick summary',
			})}
		>
			{#each quickPeaks as peak (`${peak.deviceName}-${peak.channel}`)}
				<DashInfoCard
					class="flex-1 min-width-0"
					icon="fas fa-bolt"
					value={peak.live?.value ?? '—'}
					infoText={`${peak.deviceName} · ${peak.label}`}
					color="--color-grid"
				/>
			{/each}
		</DashInfoWidget>
	{/if}
{:else}
	<EmWidgetShell
		widgetClass="energy-widget-peak"
		title={resolvedTitle}
		infoText={$format('energy_monitoring_peak_info', {
			default:
				'Live peak snapshots and 5-minute averages from power meters with peak measurement enabled.',
		})}
	>
		{#if !hasDevices}
			<EmWidgetState
				kind="setup"
				minHeight="160px"
				message={$format('energy_monitoring_peak_empty', {
					default:
						'Enable peak power measurement on a power meter to see live and average peaks.',
				})}
				actionHref={hasPowerMeterDevices($devices)
					? getEnergyTariffSettingsHref($showNewLayout)
					: ENERGY_ADD_DEVICE_HREF}
				actionLabel={hasPowerMeterDevices($devices)
					? $format('button_configure', { default: 'Configure' })
					: $format('button_add_device', { default: 'Add device' })}
			/>
		{:else if loading && !devicePanels.length}
			<div class="em-peak-loading">
				<Loader size="m" />
			</div>
		{:else if loadError}
			<EmWidgetState
				kind="error"
				minHeight="160px"
				message={$format('energy_monitoring_peak_error', {
					default: 'Peak power snapshots could not be loaded. Please try again.',
				})}
				onRefresh={loadSnapshots}
			/>
		{:else if variant === 'phaseBalance' && !phaseBalanceModel}
			<EmWidgetState
				kind="setup"
				minHeight="160px"
				message={$format('energy_monitoring_phase_peak_empty', {
					default: 'Enable peak measurement on a multi-phase meter to compare phase balance.',
				})}
				actionHref={getEnergyTariffSettingsHref($showNewLayout)}
				actionLabel={$format('button_configure', { default: 'Configure' })}
			/>
		{:else if overviewModel}
			{#if variant === 'byMeter'}
				<EmPeakPowerByMeter rows={meterRows} />
			{:else if variant === 'phaseBalance' && phaseBalanceModel}
				<EmPhasePeakBalance model={phaseBalanceModel} />
			{:else}
				<EmPeakPowerOverview model={overviewModel} />
			{/if}
		{:else}
			<EmWidgetState
				minHeight="160px"
				message={$format('energy_monitoring_peak_waiting', {
					default: 'No peak readings are available yet.',
				})}
			/>
		{/if}
	</EmWidgetShell>
{/if}

<style>
	.em-peak-loading {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: calc(var(--spacing-xl) * 5);
	}
</style>
